import { useState, useEffect } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  InputGroup,
  InputLeftElement,
  Spinner,
  Text,
  useToast,
  Button,
  HStack,
  ButtonGroup,
  Badge,
  Tooltip,
  Card,
  CardBody
} from '@chakra-ui/react';
import { SearchIcon, RepeatIcon } from '@chakra-ui/icons';
import { getTrades } from '../api';

// 格式化数字
const formatNumber = (num) => {
  if (num === null || num === undefined) return '-';
  return Number(num).toLocaleString('zh-CN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const Trades = () => {
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchSymbol, setSearchSymbol] = useState('');
  const [showAllTrades, setShowAllTrades] = useState(false);
  const toast = useToast();

  const fetchTrades = async () => {
    try {
      setLoading(true);
      const params = {
        symbol: searchSymbol || undefined,
        date: showAllTrades ? undefined : new Date().toISOString()
      };
      const data = await getTrades(params);
      setTrades(data);
    } catch (error) {
      toast({
        title: '获取交易记录失败',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrades();
  }, [searchSymbol, showAllTrades]);

  const handleRefresh = () => {
    if (!loading) {
      fetchTrades();
    }
  };

  return (
    <Card>
      <CardBody>
        <HStack mb={4} spacing={4} justify="space-between">
          <Text fontSize="xl" fontWeight="bold">交易记录</Text>
          <HStack spacing={4}>
            <InputGroup maxW="300px">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.300" />
              </InputLeftElement>
              <Input
                placeholder="搜索标的..."
                value={searchSymbol}
                onChange={(e) => setSearchSymbol(e.target.value)}
                size="sm"
              />
            </InputGroup>
            <ButtonGroup isAttached variant="outline" size="sm">
              <Button
                onClick={() => setShowAllTrades(false)}
                colorScheme={!showAllTrades ? 'blue' : 'gray'}
              >
                今日
              </Button>
              <Button
                onClick={() => setShowAllTrades(true)}
                colorScheme={showAllTrades ? 'blue' : 'gray'}
              >
                全部
              </Button>
            </ButtonGroup>
            <Button
              leftIcon={<RepeatIcon />}
              onClick={handleRefresh}
              isLoading={loading}
              colorScheme="blue"
              variant="outline"
              size="sm"
            >
              刷新
            </Button>
          </HStack>
        </HStack>

        {loading ? (
          <Spinner />
        ) : trades.length === 0 ? (
          <Text>暂无交易记录</Text>
        ) : (
          <Box overflowX="auto">
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>标的</Th>
                  <Th>方向</Th>
                  <Th isNumeric>数量</Th>
                  <Th isNumeric>价格</Th>
                  <Th isNumeric>实现盈亏</Th>
                  <Th isNumeric>手续费</Th>
                  <Th>时间</Th>
                </Tr>
              </Thead>
              <Tbody>
                {trades.map((trade) => (
                  <Tr key={trade.id}>
                    <Td>
                      <Badge colorScheme="purple" fontSize="sm">
                        {trade.symbol}
                      </Badge>
                    </Td>
                    <Td>
                      <Badge
                        colorScheme={trade.direction === 'buy' ? 'green' : 'red'}
                        variant="subtle"
                        fontSize="sm"
                      >
                        {trade.direction === 'buy' ? '买入' : '卖出'}
                      </Badge>
                    </Td>
                    <Td isNumeric>{formatNumber(Math.abs(trade.quantity))}</Td>
                    <Td isNumeric>{formatNumber(trade.price)}</Td>
                    <Td isNumeric>
                      <Badge
                        colorScheme={trade.realized_pnl >= 0 ? 'green' : 'red'}
                        variant="outline"
                        fontSize="sm"
                      >
                        {formatNumber(trade.realized_pnl)}
                      </Badge>
                    </Td>
                    <Td isNumeric>
                      <Tooltip label="交易手续费" placement="top">
                        {formatNumber(trade.commission)}
                      </Tooltip>
                    </Td>
                    <Td fontSize="sm">{new Date(trade.created_at).toLocaleString()}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}
      </CardBody>
    </Card>
  );
};

export default Trades; 